import { useState } from "react";
import { useForm } from "react-hook-form";
import { formatMWh } from "@/utils/formatMWh.ts";
import { Button, Modal as MuiModal, Typography } from "@mui/material";
import Modal from "@/components/Modal";
import FormNumberInput from "@/components/FormNumberInput";
import FormInput from "@/components/FormInput";
import ButtonsSection from "@/components/Modal/ButtonsSection.tsx";
import SvgCardDeduct from "@/assets/icons/SVGCardDeduct.tsx";
import SuccessOperationModal from "@/components/SuccessOperationModal";
import { useBudgetStore } from "@/store/budgetStore.ts";
import { deductTotalBudget } from "@/api/budgetWidgetApi";
import { getTransactionLabel } from "@/utils/getTransactionLabel.ts";

type FundsFormType = {
  funds: string;
  note: string;
};
const DeductFunds = ({ disabled = false }: { disabled?: boolean }) => {
  const [open, setOpen] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const budgetData = useBudgetStore((state) => state.budgetData);
  const setBudgetData = useBudgetStore((state) => state.setBudgetData);
  const getTransactions = useBudgetStore((state) => state.getTransactions);

  const { handleSubmit, reset, control, watch } = useForm<FundsFormType>();
  const funds = watch("funds");
  const onSubmit = async (data: any) => {
    try {
      const amount = Math.round(Number(data?.funds) * 100);
      const response = await deductTotalBudget({
        amount,
        note: data?.note,
      });
      setBudgetData(response?.data?.updatedBudget);
      getTransactions();
      handleClose();
      setOpenSuccess(true);
    } catch (e) {
      console.error(e);
    }
  };
  const handleClose = () => {
    reset({ funds: "", note: "" });
    setOpen(false);
  };

  const isFundsEmpty = !(Number(funds) * 100 > 0);

  const currentBalance = budgetData?.total;

  const formattedCurrentBalance = formatMWh(currentBalance);
  const formattedNewBalance = formatMWh(currentBalance - Number(funds));

  return (
    <>
      <Button
        startIcon={<SvgCardDeduct />}
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: "14px",
          padding: "8px 12px",
        }}
        variant={"outlined"}
        onClick={() => setOpen(true)}
        disabled={disabled}
        size="small"
      >
        Deduct
      </Button>
      <Modal open={open} onClose={handleClose} title={"Deduct funds"}>
        <>
          <FormNumberInput
            name={"funds"}
            control={control}
            label={"Amount of funds"}
            placeholder={"0.00 MWh"}
          />
          {!isFundsEmpty && (
            <>
              <Typography
                sx={{ display: "block", marginTop: "2px" }}
                variant={"helperText"}
              >
                Current balance: {formattedCurrentBalance}
              </Typography>
              <Typography sx={{ display: "block" }} variant={"helperText"}>
                Balance after operation: {formattedNewBalance}
              </Typography>
            </>
          )}
          <FormInput
            name={"note"}
            control={control}
            label={"Note"}
            placeholder={"Add note..."}
            multiline
            rows={4}
          />
          <ButtonsSection
            onCancel={handleClose}
            actionButton={
              <Button
                sx={{ width: "100%" }}
                onClick={handleSubmit(onSubmit)}
                variant={"contained"}
                disabled={isFundsEmpty || Number(funds) > currentBalance}
              >
                Deduct funds
              </Button>
            }
          />
        </>
      </Modal>
      <MuiModal open={openSuccess} onClose={() => setOpenSuccess(false)}>
        <SuccessOperationModal
          onClose={() => setOpenSuccess(false)}
          title={`Funds successfully ${getTransactionLabel("deduct")}`}
          subtitle={`Your current balance is ${formattedCurrentBalance}`}
        />
      </MuiModal>
    </>
  );
};

export default DeductFunds;
